import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
} from "react";
import { fetchAuthSession } from "aws-amplify/auth";
import { UserRole } from "../roleConfig";
import { Flex, Text } from "@chakra-ui/react";
import colors from "../../theme/foundation/colors";
import {
  fetchUserRoleWithAdminCheck,
  EntityUserInfo,
  entityMapToFlatArray,
} from "../api/fetchUserRole";
import { keyframes } from "@emotion/react";

interface Identity {
  dateCreated: string;
  userId: string;
  providerName: string;
  providerType: string;
  issuer: string;
  primary: string;
}

interface UserInfo {
  email: string;
  given_name: string;
  family_name: string;
  email_verified: string;
  sub: string;
  identities: Identity[];
  roles: UserRole[];
  name?: string;
  upload_accesses: string;
  adminData?: {
    entityUserMap: Map<string, EntityUserInfo>;
    flatEntityData: ReturnType<typeof entityMapToFlatArray>;
    userEmail?: string;
    userName?: string;
  };
  apiError?: string;
}

const UserContext = createContext<{
  userInfo: UserInfo | null;
  refreshUserData: () => Promise<void>;
} | undefined>(undefined);

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [dataLoaded, setDataLoaded] = useState(false);

  const refreshUserData = useCallback(async () => {
    try {
      const session = await fetchAuthSession();

      if (!session.tokens?.idToken) {
        throw new Error("No session");
      }

      const payload = session.tokens.idToken.payload;


      const email = typeof payload.email === "string" ? payload.email : "";
      let given_name = typeof payload.given_name === "string" ? payload.given_name : "";
      const family_name = typeof payload.family_name === "string" ? payload.family_name : "";
      const email_verified = typeof payload.email_verified === "string" ? payload.email_verified : "";
      const sub = typeof payload.sub === "string" ? payload.sub : ""


      const parsedIdentities: Identity[] = [];

      let userRoles: UserRole[] = [];
      let upload_accesses = "";
      let adminData;
      let apiError;

      if (email) {
        try {
          const lowercaseEmail = email.toLowerCase();

          // ✅ Store in localStorage (optional)
          try {
            localStorage.setItem(
              "userInfo",
              JSON.stringify({
                email: lowercaseEmail,
                given_name,
                family_name,
              })
            );
          } catch {}

          const result = await fetchUserRoleWithAdminCheck(lowercaseEmail);

          userRoles = result.userResult.roles;
          given_name = result.userResult.name || given_name;

          upload_accesses = Array.isArray(result.userResult.upload_accesses)
            ? result.userResult.upload_accesses.join(",")
            : result.userResult.upload_accesses || "";

          adminData = result.adminData;

          if (adminData && adminData.entityUserMap) {
            if (!adminData.flatEntityData) {
              adminData.flatEntityData = entityMapToFlatArray(
                adminData.entityUserMap
              );
            }
          }
        } catch (error: any) {
          apiError = error?.message || "Error fetching roles";
          userRoles = [];
        }
      }

      setUserInfo({
        email,
        given_name,
        family_name,
        email_verified,
        sub,
        identities: parsedIdentities,
        roles: userRoles,
        upload_accesses,
        adminData,
        apiError,
      });
    } catch (err) {
      setUserInfo(null);
    } finally {
      setLoading(false);
      setDataLoaded(true);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      try {
        const session = await fetchAuthSession();

        if (session.tokens?.idToken) {
          await refreshUserData(); // ✅ wait properly
        } else {
          setLoading(false);
          setDataLoaded(true);
        }
      } catch {
        setLoading(false);
        setDataLoaded(true);
      }
    };

    init();
  }, [refreshUserData]);

  if (loading || !dataLoaded) {
    return (
      <Flex
        position="fixed"
        top="0"
        left="0"
        right="0"
        bottom="0"
        zIndex="9999"
        justifyContent="center"
        alignItems="center"
        flexDirection="column"
        animation={`${glowAnimation} 2.5s ease-in-out infinite`}
      >
        <Text fontSize="xl" fontWeight="semibold" color="white">
          Loading...
        </Text>
      </Flex>
    );
  }

  return (
    <UserContext.Provider value={{ userInfo, refreshUserData }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context.userInfo;
};

export const useRefreshUserData = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useRefreshUserData must be used within a UserProvider");
  }
  return context.refreshUserData;
};

export const withUser = <P extends object>(
  Component: React.ComponentType<P & { user: UserInfo | null }>
) => {
  return (props: P) => {
    const user = useUser();
    return <Component {...props} user={user} />;
  };
};

const glowAnimation = keyframes`
  0% { background-color: white; }
  33% { background-color: ${colors.lightteal}; }
  66% { background-color: ${colors.darkteal}; }
  100% { background-color: white; }
`;